import React, { useState, useEffect, useMemo, useCallback } from 'react';
import CreationModal from '../../components/ui/CreationModal';
import { isWithdrawalCategory } from '../../utils/categoryUtils';
import { categoryDetailAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const TransactionCreationModal = ({ open, onClose, onSubmit, loading = false }) => {
  const [categories, setCategories] = useState([]);
  const { user } = useAuth();

  const fetchCategories = useCallback(async () => {
    try {
      let response;
      
      // For agents, get category details for their assigned caisses only
      if (user?.role === 'AGENT' && user?.assignedCaisses?.length > 0) {
        // Get the first assigned caisse ID (agents typically have one caisse)
        const caisseId = user.assignedCaisses[0]?.caisse?.id || user.assignedCaisses[0]?.id;
        response = await categoryDetailAPI.getForFiltersByCaisse(caisseId);
      } else {
        // For managers and other roles, get all category details
        response = await categoryDetailAPI.getAll();
      }
      
      // Handle different response structures
      const categoriesData = response?.data || response;
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  }, [user]);

  // Fetch categories when modal opens
  useEffect(() => {
    if (open) {
      fetchCategories();
    }
  }, [open, fetchCategories]);

  // Field configuration for transaction creation
  const transactionFields = useMemo(() => [
    {
      name: 'category',
      label: 'Catégorie',
      type: 'select',
      required: true,
      placeholder: 'Catégorie',
      options: Array.isArray(categories)
        ? categories
            .filter(cat => !isWithdrawalCategory(cat))
            .map(categoryDetail => ({ 
        value: categoryDetail.id, 
        label: categoryDetail.category?.label || categoryDetail.label 
            }))
        : []
    },
    {
      name: 'amount',
      label: 'Montant',
      type: 'number',
      required: true,
      placeholder: 'Montant'
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: true,
      placeholder: 'Description'
    }
  ], [categories]);

  const handleSubmit = async (formData) => {
    // Get the agent's assigned caisse ID
    let caisseId = null;
    if (user?.role === 'AGENT' && user?.assignedCaisses?.length > 0) {
      caisseId = user.assignedCaisses[0]?.caisse?.id || user.assignedCaisses[0]?.id;
    }
    
    // Find the category detail data for validation
    const selectedCategoryDetail = categories.find(cat => cat.id === formData.category);
    const categoryName = selectedCategoryDetail?.category?.label || selectedCategoryDetail?.label || 'Catégorie inconnue';
    
    const transactionData = {
      caisseId: caisseId,
      categoryDetailId: formData.category,
      categoryName: categoryName,
      categoryData: selectedCategoryDetail, // Pass full category detail data including caisseFactor
      amount: parseFloat(formData.amount),
      description: formData.description.trim()
    };
    onSubmit(transactionData);
  };

  return (
    <CreationModal
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title="Créer une transaction"
      fields={transactionFields}
      submitButtonText="Créer une transaction"
      loading={loading}
      fieldsPerRow={2}
      initialData={useMemo(() => ({ 
        category: '', 
        amount: '', 
        description: '' 
      }), [])}
    />
  );
};

export default TransactionCreationModal;
